import React, {useEffect} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {createPersonnelInvite} from '@/entities/personnel/api';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {fonts} from '@/shared/ui/fonts';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Mints a one-time invite code and shows it. The employee types this code into the app
 * (Онбординг → «Ввести код»); redeeming it links their account to this record so they
 * see their own attendance — nothing more. No clipboard dependency: the code is
 * long-press-selectable, which every Android keyboard already handles.
 *
 * Mounted fresh per open (keyed by the caller), so it mints exactly one code each time.
 */
const styles = StyleSheet.create({
  body: {gap: spacing(4)},
  // The code is the whole point of this sheet, so it gets the teal wash, the biggest
  // type in the app and letter-spacing wide enough to read a character at a time out
  // loud over a phone — which is how these codes actually travel.
  codeBox: {
    alignItems: 'center',
    paddingVertical: spacing(6),
    borderRadius: radii.lg,
    borderWidth: 1,
  },
  code: {fontFamily: fonts.mono.medium, fontSize: 34, letterSpacing: 4},
  center: {paddingVertical: spacing(6), alignItems: 'center', gap: spacing(3)},
});

export function InviteSheet({
  companyId,
  recordId,
  visible,
  onClose,
}: {
  companyId: string;
  recordId: string;
  visible: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  const invite = useMutation({
    mutationFn: () => createPersonnelInvite(companyId, recordId),
  });

  // One code per open; the caller keys this component so mutate fires once.
  useEffect(() => {
    if (visible) {
      invite.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  let body: React.ReactNode;
  if (invite.isPending || invite.isIdle) {
    body = (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={p.primary} />
      </View>
    );
  } else if (invite.isError) {
    body = (
      <View style={styles.center}>
        <Banner tone="error" title={messageOf(invite.error, t('errors.unknown'))} />
        <AppButton title={t('actions.retry')} tone="neutral" onPress={() => invite.mutate()} />
      </View>
    );
  } else {
    const data = invite.data;
    body = (
      <View style={styles.body}>
        <View style={[styles.codeBox, {backgroundColor: p.accent, borderColor: p.primary}]}>
          <AppText selectable style={styles.code} color={p.primary}>
            {data.code}
          </AppText>
        </View>
        <AppText variant="caption">
          {t('personnel.inviteExpires', {value: data.expires_at})}
        </AppText>
        <AppText variant="body" color={p.mutedForeground}>
          {t('personnel.inviteHint')}
        </AppText>
      </View>
    );
  }

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={t('personnel.inviteTitle')}
      testID="invite-sheet">
      {body}
      <AppButton
        title={t('actions.close')}
        tone="neutral"
        onPress={onClose}
        style={{marginTop: spacing(4)}}
      />
    </Sheet>
  );
}
